import * as Phaser from 'phaser';
import { createLogger } from '../utils/Logger';
import { COMBAT_VISUAL_LIMITS } from '../config/GameConfig';

export class ParticleManager {
  private readonly emitters = new Map<string, Phaser.GameObjects.Particles.ParticleEmitter>();
  private isDisposed = false;
  private reducedFlashes = false;
  private readonly logger = createLogger('ParticleManager');

  constructor(private readonly scene: Phaser.Scene) {
    this.initEmitters();
  }

  public playEffect(key: string, x: number, y: number, burstCount?: number): void {
    if (this.isDisposed) return;
    const emitter = this.emitters.get(key);
    if (!emitter) {
      this.logger.warn('playEffect:unknown-effect-key', { key, x, y, burstCount });
      return;
    }

    const baseQuantity = Number(emitter.config.quantity ?? 0);
    const count = Number.isFinite(burstCount) && burstCount !== undefined
      ? Math.max(0, Math.round(burstCount))
      : Math.max(0, Math.round(baseQuantity));

    emitter.explode(this.reducedFlashes ? Math.min(count, 3) : count, x, y);
  }

  public setReducedFlashes(value: boolean): void {
    this.reducedFlashes = value;
    this.emitters.forEach(emitter => {
      if (value) emitter.killAll?.();
      emitter.setAlpha?.(value ? 0.4 : 1);
      emitter.setBlendMode?.('NORMAL');
    });
  }

  public setPaused(paused: boolean): void {
    this.emitters.forEach((emitter, key) => {
      if (key !== 'levelup' && key !== 'chestReward') emitter.active = !paused;
    });
    this.logger.debug('pause:applied', { paused, emitters: this.emitters.size });
  }

  public getDiagnosticsSnapshot() {
    return { emitters: this.emitters.size, alive: [...this.emitters.values()].reduce((count, emitter) => count + emitter.getAliveParticleCount(), 0), maxAlive: [...this.emitters.keys()].reduce((count, key) => count + (key === 'chestReward' ? COMBAT_VISUAL_LIMITS.rewardParticles : COMBAT_VISUAL_LIMITS.particlesPerEmitter), 0) };
  }

  public dispose(): void {
    if (this.isDisposed) {
      this.logger.warn('dispose:already-disposed');
      return;
    }

    this.emitters.forEach((emitter, key) => {
      const destroy = (emitter as unknown as { destroy?: () => void }).destroy;
      if (typeof destroy === 'function') {
        destroy.call(emitter);
      }
      this.logger.debug('dispose:emitter-destroyed', { key });
    });
    this.emitters.clear();
    this.isDisposed = true;
    this.logger.debug('dispose:complete');
  }

  private initEmitters(): void {
    this.ensureParticleTexture('spark', 0xffffff, 4, 8);
    this.ensureParticleTexture('smoke', 0x888888, 6, 12);

    this.registerEmitter('hit', 'spark', { lifespan: 300, speed: { min: 50, max: 150 }, scale: { start: 1, end: 0 }, quantity: 5, emitting: false });
    this.registerEmitter('crit', 'spark', { lifespan: 360, speed: { min: 80, max: 220 }, scale: { start: 1.2, end: 0 }, quantity: 10, emitting: false, tint: 0xffd24d });
    this.registerEmitter('burn', 'spark', { lifespan: 420, speed: { min: 40, max: 120 }, scale: { start: 1, end: 0 }, quantity: 8, emitting: false, tint: 0xff8844 });
    this.registerEmitter('slow', 'spark', { lifespan: 420, speed: { min: 40, max: 120 }, scale: { start: 1, end: 0 }, quantity: 8, emitting: false, tint: 0x88ccff });
    this.registerEmitter('chain', 'spark', { lifespan: 320, speed: { min: 60, max: 180 }, scale: { start: 1, end: 0 }, quantity: 12, emitting: false, tint: 0xffee88 });
    this.registerEmitter('area', 'spark', { lifespan: 380, speed: { min: 60, max: 170 }, scale: { start: 1, end: 0 }, quantity: 12, emitting: false, tint: 0xffb366 });
    this.registerEmitter('death', 'smoke', { lifespan: 600, speed: { min: 20, max: 100 }, scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 }, quantity: 15, emitting: false });
    this.registerEmitter('bossDeath', 'smoke', { lifespan: 760, speed: { min: 50, max: 220 }, scale: { start: 1.4, end: 0 }, alpha: { start: 1, end: 0 }, quantity: 24, emitting: false });
    this.registerEmitter('levelup', 'spark', { lifespan: 2000, speed: 200, scale: { start: 2, end: 0 }, blendMode: 'ADD', quantity: 30, emitting: false });
    this.registerEmitter('chestReward', 'spark', { lifespan: 1100, speed: { min: 90, max: 210 }, scale: { start: 1.3, end: 0 }, blendMode: 'ADD', quantity: 18, emitting: false });
  }

  private registerEmitter(key: string, texture: string, config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig): void {
    const limit = key === 'chestReward' ? COMBAT_VISUAL_LIMITS.rewardParticles : COMBAT_VISUAL_LIMITS.particlesPerEmitter;
    const emitter = this.scene.add.particles(0, 0, texture, { maxParticles: limit, maxAliveParticles: limit, ...config });
    this.emitters.set(key, emitter);
    this.logger.debug('initEmitters:emitter-created', { key, texture, quantity: config.quantity });
  }

  private ensureParticleTexture(key: string, color: number, radius: number, size: number): void {
    if (this.scene.textures.exists(key)) {
      return;
    }

    const graphics = this.scene.make.graphics({ x: 0, y: 0 });
    graphics.fillStyle(color);
    graphics.fillCircle(size / 2, size / 2, radius);
    graphics.generateTexture(key, size, size);
    graphics.destroy();
    this.logger.info('initEmitters:generated-fallback-texture', { key, size });
  }
}
